import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { RegisterCompany } from '../models/agents/register-company';
import { CompanyData } from '../models/agents/company-data';
import { EconomicGroup } from '../models/agents/economic-group';
import { Pix } from '../models/agents/pix';
import { RelationshipContact } from '../models/agents/relationship-contact';
import { Authorizations } from '../models/agents/authorizations';
import { FiscalData } from '../models/agents/fiscal-data';
import { Question } from '../models/suitability/question';
import { Files } from '../models/agents/files';
import { Observable } from 'rxjs/Observable';
import { BankData } from '../models/agents/bank-data';
import { SuitabilityScore } from '../models/agents/suitability-score';
import { CreateCompanyOutput } from '../models/agents/output/create-company-output';
import { UpdateCompanyOutput } from '../models/agents/output/update-company-output';
import { Store } from '@ngxs/store';
import { User } from '../models/auth/user';
import { AuthState } from '../state-manager/state/auth.state';
import { get } from 'lodash';
import { TaxPaymentTypeEnum } from '../models/enums/investor-tax-payment-type.enum';
import { RepresentativesData } from '../models/agents/representatives-data';
import { Suitability } from '../models/suitability/suitability';
import { PatrimonialSituation } from '../models/agents/patrimonial';
import { SuitabilityWaiver } from '../models/agents/suitability-waiver';
import { ContractData } from '../models/agents/contract-data';
import { Beneficiary } from '../models/agents/beneficiary';

@Injectable()
export class CompanyService {
  userLogged: User;

  constructor(protected http: HttpClient, protected store: Store) {
    setTimeout(() => {
      this.getUserLogged();
    }, 1000);
  }

  getUserLogged() {
    this.store.select(AuthState.User).subscribe((user) => {
      this.userLogged = user;
    });
  }

  registerCompany(registerCompany: RegisterCompany) {
    registerCompany.userId = get(this.userLogged, 'userID');

    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.registerCompany;
    return this.http.post<CreateCompanyOutput>(url, registerCompany);
  }

  updateCompany(
    agentId: string,
    company: CompanyData
  ): Observable<UpdateCompanyOutput> {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.registerCompany +
      '/' +
      agentId +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.put<UpdateCompanyOutput>(url, company);
  }

  confirmRegistration(agentId: string) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.companyConfirmRegistration +
      '/' +
      agentId +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.put(url, {});
  }

  patchCompany(companyPatchJson: any) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.registerCompany +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.patch(url, companyPatchJson);
  }

  patchFiscalData(
    iof: TaxPaymentTypeEnum,
    irrf: TaxPaymentTypeEnum,
    fiscalData: FiscalData,
    agentId: string
  ) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.registerCompany +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.patch(url, {
      agentID: agentId,
      companyPatch: [
        { op: 'replace', path: '/IRRF', value: irrf },
        { op: 'replace', path: '/IOF', value: iof },
        { op: 'replace', path: '/FiscalData', value: fiscalData }
      ]
    });
  }

  getCompanyByAgent(agentId: string): Observable<CompanyData> {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.companyByAgent +
      '/' +
      agentId;

    return this.http.get<CompanyData>(url);
  }

  updateCompanySuitabilityWaiver(
    document: string
  ): Observable<SuitabilityWaiver> {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.updateCompanySuitabilityWaiver;

    return this.http.post<SuitabilityWaiver>(url, {
      document
    });
  }

  updateRepresentatives(
    agentId: string,
    representatives: Array<RepresentativesData>
  ) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.companyRepresentatives +
      '/' +
      agentId +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.put(url, representatives);
  }

  updateBankData(agentId: string, bankData: Array<BankData>) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.companyBankData +
      '/' +
      agentId +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.put(url, bankData);
  }

  updatePatrimonial(
    agentId: string,
    patrimonialSituation: PatrimonialSituation
  ) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.companyPatrimonial +
      '/' +
      agentId +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.put(url, patrimonialSituation);
  }

  updateFiles(agentId: string, files: Files[]) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.companyFiles +
      '/' +
      agentId +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.put(url, files);
  }

  disableCompanyFile(agentId: string, fileUrl: string) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.companyDisableFile +
      '/' +
      agentId +
      '/' +
      get(this.userLogged, 'userID') +
      '?fileUrl=' +
      fileUrl;

    return this.http.post(url, null);
  }

  updateEconomicGroup(agentId: string, economicGroup: EconomicGroup) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.companyEconomicGroup +
      '/' +
      agentId +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.put(url, economicGroup);
  }

  updateAuthorizations(agentId: string, authorizations: Authorizations) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.companyAuthorizations +
      '/' +
      agentId +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.put(url, authorizations);
  }

  updateSuitabilityQuestions(agentId: string, suitability: Suitability) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.companySuitabilityQuestions +
      '/' +
      agentId +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.put(url, suitability);
  }

  updateCreatedAt(agentId: string, createAt: string) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.registerCompany +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.patch(url, {
      agentID: agentId,
      companyPatch: [{ op: 'replace', path: '/CreatedAt', value: createAt }]
    });
  }

  updateSuitabilityScore(agentId: string, score: SuitabilityScore) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.companySuitabilityScore +
      '/' +
      agentId +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.put(url, score);
  }

  updateValidated(agentId: string, validated: boolean) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.registerCompany +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.patch(url, {
      agentID: agentId,
      companyPatch: [{ op: 'replace', path: '/Validated', value: validated }]
    });
  }

  updateSuitabilityQuestionsTrack(agentId: string, questionsTrack: any) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.companySuitabilityQuestionTrack +
      '/' +
      agentId;

    return this.http.put(url, questionsTrack);
  }

  patchRelationshipContacts(agentId: string, contacts: RelationshipContact[]) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.registerCompany +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.patch(url, {
      agentID: agentId,
      companyPatch: [
        { op: 'replace', path: '/RelationshipContacts', value: contacts }
      ]
    });
  }

  updatePixData(agentId: string, pixs: Pix[]) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.registerCompany +
      '/' +
      get(this.userLogged, 'userID');

    return this.http.patch(url, {
      agentID: agentId,
      companyPatch: [{ op: 'replace', path: '/Pixs', value: pixs }]
    });
  }

  updateContractData(agentId: string, contractData: ContractData) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.companyContractData +
      `/${agentId}/${this.userLogged.userID}`;

    return this.http.put(url, contractData);
  }

  updateBeneficiariesData(agentId: string, beneficiaries: Beneficiary[]) {
    const url =
      environment.apiCadastroSettings.url +
      environment.apiCadastroSettings.companyBeneficiariesData +
      `/${agentId}/${this.userLogged.userID}`;

    return this.http.put(url, { beneficiaries });
  }

  generateInvestorLevelFile(agentID: string): Observable<any> {
    const url = environment.apiCadastroSettings.url + environment.apiCadastroSettings.companyGenerateInvestorLevelFile;

    return this.http.post(url, {agentID, userId: get(this.userLogged, 'userID')});
  }

  generateSuitabilityFile(agentId: string, virtualRawFingerprint: string): Observable<any> {
    const url = environment.apiCadastroSettings.url + environment.apiCadastroSettings.companyGenerateSuitabilityFile;

    return this.http.post(url, {agentId, virtualRawFingerprint, userId: get(this.userLogged, 'userID')});
  }
}
